// Genesis Appliance Repair · Service area data
// One module powers all /service-areas/:slug routes.
// Each area carries hero copy, body paragraphs, neighborhoods, geo, SEO and a local-insight callout
// in both EN and ES so the page reads naturally for each language.

export const SERVICE_AREAS = [
  {
    slug: "hawaii-county",
    type: "county",
    coords: { lat: 19.5429, lng: -155.6659 },
    image:
      "https://images.unsplash.com/photo-1542209025-7b97cd6f7045?auto=format&fit=crop&w=1600&q=85",
    name: { en: "Hawaii County", es: "Condado de Hawái" },
    shortName: { en: "Hawaii County, HI", es: "Condado de Hawái, HI" },
    hero: {
      en: {
        eyebrow: "Service area · The Big Island",
        title: "Appliance repair across all of Hawaii County.",
        lead: "From Hilo to Hawi, from Volcano to Captain Cook, our certified technicians serve every district of the Big Island with the same calm, careful, OEM-grade work.",
      },
      es: {
        eyebrow: "Área de servicio · La Isla Grande",
        title: "Reparación de electrodomésticos en todo el Condado de Hawái.",
        lead: "De Hilo a Hawi, de Volcano a Captain Cook, nuestros técnicos certificados atienden cada distrito de la Isla Grande con el mismo trabajo calmo, cuidadoso y de calidad OEM.",
      },
    },
    neighborhoods: [
      "Hilo",
      "Pahoa",
      "Volcano",
      "Naalehu",
      "Ocean View",
      "Captain Cook",
      "Kealakekua",
      "Kailua-Kona",
      "Holualoa",
      "Waimea (Kamuela)",
      "Honokaa",
      "Hawi",
    ],
    bodyParagraphs: {
      en: [
        "Hawaii County is the largest of the Hawaiian Islands and the most varied: coastal, mountainous, dry on the west, wet on the east. Each microclimate puts a different kind of stress on the appliances inside its homes.",
        "Our weekly routes thread through all four major districts (North Kohala, South Kohala, Hamakua, Puna) so we can reach most properties within 24 to 48 hours of a call. For urgent issues, we maintain a same-day dispatch list with a daily quota for emergency visits.",
        "Every Genesis truck stocks the most common OEM parts for refrigerators, washers, dryers, dishwashers, and ranges. If we don't have your specific part on hand, we'll source it from our island warehouse and return on a scheduled second visit at no additional dispatch fee.",
      ],
      es: [
        "El Condado de Hawái es la isla más grande del archipiélago y la más variada: costa, montaña, seco al oeste, húmedo al este. Cada microclima impone un tipo distinto de esfuerzo a los electrodomésticos del hogar.",
        "Nuestras rutas semanales recorren los cuatro distritos principales (Kohala Norte, Kohala Sur, Hamakua, Puna), por lo que llegamos a la mayoría de propiedades en 24 a 48 horas. Para casos urgentes mantenemos una cuota diaria de visitas de emergencia.",
        "Cada camión Genesis lleva las piezas OEM más comunes para refrigeradores, lavadoras, secadoras, lavavajillas y estufas. Si no tenemos su pieza, la traemos del almacén isleño y regresamos en una segunda visita sin cargo adicional.",
      ],
    },
    insight: {
      en: {
        title: "Why Big Island appliances need a different playbook",
        body: "Mainland service protocols assume a uniform climate. Our island doesn't have one. We adjust diagnostics for elevation (Volcano vs Kona), salt exposure (coastal vs inland), and seasonal humidity swings, three factors mainland service manuals barely mention.",
      },
      es: {
        title: "Por qué los electrodomésticos de la Isla Grande necesitan un manual distinto",
        body: "Los protocolos continentales asumen un clima uniforme. Nuestra isla no lo tiene. Ajustamos diagnósticos por altitud (Volcano vs Kona), exposición salina (costa vs interior) y oscilaciones de humedad estacional, tres factores que los manuales continentales apenas mencionan.",
      },
    },
    seo: {
      en: {
        title: "Appliance Repair Hawaii County, HI | Genesis Technicians",
        description:
          "Certified appliance repair across all of Hawaii County: Hilo, Kona, Waimea, Captain Cook, Volcano. OEM parts, fast dispatch, family-owned local service.",
      },
      es: {
        title:
          "Reparación de Electrodomésticos en el Condado de Hawái | Genesis",
        description:
          "Servicio certificado en todo el Condado de Hawái: Hilo, Kona, Waimea, Captain Cook, Volcano. Piezas OEM, despacho rápido, empresa familiar local.",
      },
    },
  },
  {
    slug: "kailua-kona",
    type: "city",
    coords: { lat: 19.6398, lng: -155.9931 },
    image:
      "https://images.unsplash.com/photo-1597040663342-c1e30a6c7e54?auto=format&fit=crop&w=1600&q=85",
    name: { en: "Kailua-Kona", es: "Kailua-Kona" },
    shortName: { en: "Kailua-Kona, HI", es: "Kailua-Kona, HI" },
    hero: {
      en: {
        eyebrow: "Service area · West Hawaii",
        title: "Premium appliance repair in Kailua-Kona.",
        lead: "Coastal living, high humidity, and salt-laden trade winds put unique demands on Kona appliances. Our technicians know the terrain, and the specific models that thrive here, better than anyone.",
      },
      es: {
        eyebrow: "Área de servicio · Oeste de Hawái",
        title: "Reparación premium de electrodomésticos en Kailua-Kona.",
        lead: "Vida costera, alta humedad y vientos alisios cargados de sal imponen demandas únicas a los electrodomésticos de Kona. Nuestros técnicos conocen el terreno y los modelos que prosperan aquí mejor que nadie.",
      },
    },
    neighborhoods: [
      "Kailua-Kona village",
      "Holualoa",
      "Kahalu'u-Keauhou",
      "Honokōhau",
      "Keahole",
      "Honalo",
    ],
    bodyParagraphs: {
      en: [
        "Kailua-Kona is the busiest service zone on the Big Island. We schedule daily route days here and most service calls are completed inside 24 hours, often the same day if you book before noon.",
        "Coastal Kona homes show a specific pattern of appliance wear: refrigerator coils that need quarterly clearing, washer gaskets that age faster than published lifespans, and dishwashers that fight harder against mineral-heavy well water in the upslope subdivisions.",
        "Our Kona-stocked van keeps the most common parts for Samsung, LG, Whirlpool, GE, and Bosch, the five brands we service most often in this corridor.",
      ],
      es: [
        "Kailua-Kona es la zona de servicio más activa de la Isla Grande. Programamos días de ruta diarios y la mayoría de llamadas se resuelven en 24 horas, a menudo el mismo día si reserva antes del mediodía.",
        "Los hogares costeros de Kona muestran un patrón específico de desgaste: bobinas de refrigerador que necesitan limpieza trimestral, empaques de lavadora que envejecen antes de lo previsto y lavavajillas que luchan contra el agua mineralizada de los pozos en las subdivisiones de altura.",
        "Nuestra camioneta de Kona lleva las piezas más comunes para Samsung, LG, Whirlpool, GE y Bosch, las cinco marcas que más reparamos en este corredor.",
      ],
    },
    insight: {
      en: {
        title: "Salt air is the quiet enemy of Kona refrigeration",
        body: "Trade winds carry fine salt particles up the coast. They settle on condenser coils, fan motors, and exposed electronics, accelerating corrosion. We treat every Kona service call as an opportunity to inspect for early salt damage, often catching issues before they trigger a real failure.",
      },
      es: {
        title: "El aire salino es el enemigo silencioso de la refrigeración en Kona",
        body: "Los vientos alisios llevan partículas finas de sal por la costa. Se asientan en bobinas, motores de ventilador y electrónica expuesta, acelerando la corrosión. Tratamos cada visita en Kona como una oportunidad de detectar daño temprano por sal.",
      },
    },
    seo: {
      en: {
        title: "Appliance Repair Kailua-Kona HI | Genesis (Same-Day)",
        description:
          "Same-day appliance repair in Kailua-Kona. Certified technicians, OEM parts, family-owned. Refrigerators, washers, dryers, dishwashers, ranges & ovens.",
      },
      es: {
        title:
          "Reparación de Electrodomésticos en Kailua-Kona | Genesis (Mismo Día)",
        description:
          "Reparación el mismo día en Kailua-Kona. Técnicos certificados, piezas OEM, empresa familiar. Refrigeradores, lavadoras, secadoras, lavavajillas, hornos.",
      },
    },
  },
  {
    slug: "waimea",
    type: "city",
    coords: { lat: 20.0237, lng: -155.662 },
    image:
      "https://images.unsplash.com/photo-1611605645802-c21be743c321?auto=format&fit=crop&w=1600&q=85",
    name: { en: "Waimea (Kamuela)", es: "Waimea (Kamuela)" },
    shortName: { en: "Waimea, HI", es: "Waimea, HI" },
    hero: {
      en: {
        eyebrow: "Service area · North Hawaii",
        title: "Appliance repair in Waimea and Kohala.",
        lead: "Higher elevation, cooler nights, paniolo ranch country. Waimea homes have their own appliance rhythms, and we calibrate every service visit to match them.",
      },
      es: {
        eyebrow: "Área de servicio · Norte de Hawái",
        title: "Reparación de electrodomésticos en Waimea y Kohala.",
        lead: "Mayor altitud, noches frescas, tierra de ranchos paniolo. Los hogares de Waimea tienen su propio ritmo, y calibramos cada visita para coincidir con él.",
      },
    },
    neighborhoods: [
      "Waimea (Kamuela)",
      "Puako",
      "Hawi",
      "Kawaihae",
      "Honokaa",
      "Kapaau",
    ],
    bodyParagraphs: {
      en: [
        "We run a dedicated Waimea route every Tuesday and Thursday, with overflow capacity for emergency calls on other weekdays. Most Waimea visits are scheduled within 48 hours.",
        "At Waimea's elevation, the temperature gradient between kitchen and outside is much smaller than coastal Kona. That changes how refrigerators behave and how dryers vent. We tune both accordingly.",
        "Waimea also tends to have larger lot sizes and more outbuilding-style laundry rooms. Our technicians arrive prepared to service detached structures and long appliance feed lines.",
      ],
      es: [
        "Operamos una ruta dedicada en Waimea los martes y jueves, con capacidad adicional para emergencias los demás días. La mayoría de visitas se agendan en 48 horas.",
        "A la altitud de Waimea, el gradiente de temperatura entre la cocina y el exterior es mucho menor que en la costa de Kona. Esto cambia el comportamiento de refrigeradores y la ventilación de secadoras. Ajustamos ambos en consecuencia.",
        "Waimea también tiene lotes más grandes y lavanderías frecuentemente en estructuras separadas. Nuestros técnicos llegan preparados para atender estructuras independientes y líneas de alimentación largas.",
      ],
    },
    insight: {
      en: {
        title: "Cooler nights, longer dryer cycles",
        body: "Waimea homes run laundry through cooler ambient temperatures than Kona homes. That extends dryer cycles by 10 to 20 percent on average. We confirm vent path geometry and heating element calibration on every Waimea dryer service to keep cycles efficient.",
      },
      es: {
        title: "Noches más frescas, ciclos de secado más largos",
        body: "Los hogares de Waimea operan la lavandería con temperaturas ambientales más frescas que Kona. Esto extiende los ciclos de secado del 10 al 20 por ciento. Confirmamos geometría del ducto y calibración del elemento calentador en cada servicio.",
      },
    },
    seo: {
      en: {
        title: "Appliance Repair Waimea HI (Kamuela) | Genesis Technicians",
        description:
          "Certified appliance repair in Waimea, Kamuela, Hawi, and Honokaa. OEM parts, twice-weekly route days, family-owned. Book online or call (808) 764-4304.",
      },
      es: {
        title: "Reparación de Electrodomésticos en Waimea HI | Genesis",
        description:
          "Servicio certificado en Waimea, Kamuela, Hawi y Honokaa. Piezas OEM, rutas dos veces por semana, empresa familiar. Reserve en línea o llame al (808) 764-4304.",
      },
    },
  },
  {
    slug: "captain-cook",
    type: "city",
    coords: { lat: 19.4956, lng: -155.9156 },
    image:
      "https://images.unsplash.com/photo-1473773508845-188df298d2d1?auto=format&fit=crop&w=1600&q=85",
    name: { en: "Captain Cook", es: "Captain Cook" },
    shortName: { en: "Captain Cook, HI", es: "Captain Cook, HI" },
    hero: {
      en: {
        eyebrow: "Service area · South Kona",
        title: "Appliance repair in Captain Cook and South Kona.",
        lead: "South Kona's coffee belt and coastal homes deserve the same careful service as the rest of the Big Island. We schedule weekly routes from Captain Cook to Honaunau.",
      },
      es: {
        eyebrow: "Área de servicio · Kona Sur",
        title: "Reparación de electrodomésticos en Captain Cook y Kona Sur.",
        lead: "El cinturón cafetalero y los hogares costeros de Kona Sur merecen el mismo servicio cuidadoso que el resto de la Isla Grande. Operamos rutas semanales de Captain Cook a Honaunau.",
      },
    },
    neighborhoods: [
      "Captain Cook",
      "Kealakekua",
      "Honaunau",
      "Honalo",
      "Hookena",
      "Ocean View",
    ],
    bodyParagraphs: {
      en: [
        "Our South Kona route runs every Wednesday with overflow appointments on Friday. Most homes between Kealakekua and Ocean View can expect a service window inside 48 hours.",
        "South Kona's coffee farms and homestead properties often have older appliances that have been kept running well past mainland averages, sometimes 15 to 25 years. We specialize in honest repair-versus-replace conversations for these legacy units, sourcing parts from our island warehouse when factory supply has dried up.",
        "Coastal homes south of Captain Cook see the same salt-air pressure as Kailua-Kona, so refrigerator coil cleaning is part of every preventative visit.",
      ],
      es: [
        "Nuestra ruta de Kona Sur opera cada miércoles con citas adicionales los viernes. La mayoría de hogares entre Kealakekua y Ocean View tendrán ventana de servicio en 48 horas.",
        "Las granjas cafetaleras y propiedades de homestead de Kona Sur suelen tener electrodomésticos antiguos que se han mantenido funcionando más allá del promedio continental, a veces 15 a 25 años. Nos especializamos en conversaciones honestas de reparar contra reemplazar.",
        "Los hogares costeros al sur de Captain Cook experimentan la misma presión salina que Kailua-Kona, por lo que la limpieza de bobinas es parte de cada visita preventiva.",
      ],
    },
    insight: {
      en: {
        title: "Honest repair-vs-replace counsel for legacy units",
        body: "Many South Kona homes have appliances older than the technicians who service them. We don't push replacement for our convenience. If a 22-year-old fridge has 5 good years left, we'll tell you, source the part from our warehouse, and keep your unit on the road.",
      },
      es: {
        title: "Consejo honesto de reparar vs reemplazar para unidades antiguas",
        body: "Muchos hogares de Kona Sur tienen electrodomésticos más antiguos que los técnicos que los reparan. No promovemos el reemplazo por conveniencia. Si un refrigerador de 22 años tiene 5 años útiles, se lo decimos, traemos la pieza y mantenemos su unidad en operación.",
      },
    },
    seo: {
      en: {
        title: "Appliance Repair Captain Cook HI & South Kona | Genesis",
        description:
          "Weekly appliance service routes from Captain Cook to Ocean View. Certified, OEM parts, honest repair-vs-replace counsel. (808) 764-4304.",
      },
      es: {
        title: "Reparación de Electrodomésticos en Captain Cook | Genesis",
        description:
          "Rutas semanales de Captain Cook a Ocean View. Servicio certificado, piezas OEM, consejo honesto de reparar vs reemplazar. (808) 764-4304.",
      },
    },
  },
];

export const getAreaBySlug = (slug) => SERVICE_AREAS.find((a) => a.slug === slug);

// Map between an area string used in the home/contact pills and the slug page
export const AREA_PILL_TO_SLUG = {
  "Kailua-Kona, HI": "kailua-kona",
  "Captain Cook, HI": "captain-cook",
  "Waimea, HI": "waimea",
};
